import { useEffect, useState } from "react";
import api from "@/lib/api";
import { initPdfWorker, loadPdfDocument, getSafeErrorMessage } from "@/config/pdfConfig";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import GuardianDetailsPanel from "@/components/GuardianDetailsPanel";
import { FormReviewPanel, FormReviewBadge, reviewStatusLabel } from "@/components/FormReviewPanel";
import { X, Download, Loader2, AlertCircle, CheckCircle2, Shield } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

const dateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? String(iso).replace("T", " ").slice(0, 16)
    : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const Meta = ({ label, value }) => (
  <div className="flex items-center justify-between text-xs gap-3">
    <span className="text-[#757675]">{label}</span>
    <span className="font-medium text-[#1A1B1A] text-right">{value}</span>
  </div>
);

const Answer = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider block mb-1">{label}</label>
    <div className="px-3 py-2.5 rounded-xl bg-[#FAF9F6] border border-[#1A1B1A]/[0.04] text-sm text-[#1A1B1A] min-h-[36px] whitespace-pre-wrap">
      {children}
    </div>
  </div>
);

const Signature = ({ imageData, typed, testid }) => (
  <div className="px-4 py-3 rounded-xl border border-dashed border-[#1A1B1A]/15 bg-[#FAF9F6]" data-testid={testid}>
    {imageData ? (
      <img src={imageData} alt="Signature" className="max-h-24 w-auto" />
    ) : typed ? (
      <p className="text-xl italic text-[#1A1B1A]" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>{typed}</p>
    ) : (
      <p className="text-sm text-[#757675] italic">No signature provided</p>
    )}
  </div>
);

/**
 * SubmittedFormViewer - read-only view of a patient's completed form submission.
 * Generated forms render their answers; uploaded PDF forms render the completed (flattened) PDF.
 */
export default function SubmittedFormViewer({ submission: initial, onClose, onReviewed }) {
  const [submission, setSubmission] = useState(initial);
  const [pages, setPages] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  const template = submission.template || submission.template_snapshot || {};
  const isPdfForm = Boolean(template.is_uploaded_pdf || submission.flattened_pdf_path);
  const fields = template.fields || [];

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!isPdfForm) return;
    let cancelled = false;
    setPdfLoading(true);
    (async () => {
      try {
        initPdfWorker();
        const { data } = await api.get(`/forms/submissions/${submission.id}/pdf`, { responseType: "arraybuffer" });
        const pdf = await loadPdfDocument(new Uint8Array(data).slice());
        const rendered = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.6 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          rendered.push(canvas.toDataURL("image/png"));
        }
        if (!cancelled) setPages(rendered);
      } catch (err) {
        if (!cancelled) {
          setPdfError(err?.response
            ? "The completed PDF could not be loaded for this submission."
            : getSafeErrorMessage(err?.message));
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isPdfForm, submission.id]);

  const downloadPdf = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      const res = await api.get(`/forms/submissions/${submission.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const name = (res.headers?.["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "form.pdf";
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setDownloadError("PDF could not be generated. Please try again or contact support.");
    }
    setDownloading(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#FAF9F6] flex flex-col" data-testid="submitted-form-viewer">
      <div className="bg-white px-4 sm:px-6 py-3 flex items-center gap-3 border-b border-[#1A1B1A]/[0.06]">
        <div className="min-w-0">
          <h2 className="text-base text-[#1A1B1A] truncate" style={H} data-testid="submitted-form-title">
            {template.name || "Submitted form"}
          </h2>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-[#205B5A]/10 text-[#205B5A]" data-testid="submitted-badge">
              Submitted
            </span>
            <FormReviewBadge status={submission.review_status} testId="submitted-form-review-badge" />
            {(submission.signature_data || submission.typed_signature || submission.signature) && (
              <span className="text-[9px] font-semibold text-[#205B5A] bg-[#205B5A]/10 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                <CheckCircle2 className="w-2.5 h-2.5" /> Signed
              </span>
            )}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={downloadPdf}
            disabled={downloading}
            data-testid="download-submission-pdf-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-xs text-[#1A1B1A] hover:bg-[#FAF9F6] disabled:opacity-50 transition-colors"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {downloading ? "Preparing..." : "Download PDF"}
          </button>
          <button
            onClick={onClose}
            data-testid="submitted-form-close-btn"
            className="p-2 rounded-xl border border-[#1A1B1A]/10 text-[#4A4B4A] hover:bg-[#FAF9F6] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {downloadError && (
            <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3" data-testid="submission-pdf-error">
              <p className="text-xs text-red-600">{downloadError}</p>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-4 space-y-2" data-testid="submitted-form-meta">
            {submission.patient_name && <Meta label="Patient" value={submission.patient_name} />}
            <Meta label="Submitted" value={dateTime(submission.created_at)} />
            {submission.date_signed && <Meta label="Date signed" value={submission.date_signed} />}
            {submission.appointment_label && <Meta label="Appointment" value={submission.appointment_label} />}
            {!submission.appointment_label && submission.appointment_id && <Meta label="Appointment" value="Linked" />}
            <Meta label="Review status" value={reviewStatusLabel(submission.review_status)} />
            {submission.reviewed_at && <Meta label="Reviewed at" value={dateTime(submission.reviewed_at)} />}
          </div>

          {isPdfForm ? (
            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-3" data-testid="submitted-pdf-pages">
              {pdfLoading ? (
                <div className="py-16 flex justify-center" data-testid="submitted-pdf-loading">
                  <Loader2 className="w-5 h-5 text-[#757675] animate-spin" />
                </div>
              ) : pdfError ? (
                <div className="py-10 text-center" data-testid="submitted-pdf-error">
                  <AlertCircle className="w-5 h-5 text-red-400 mx-auto mb-2" />
                  <p className="text-sm text-[#4A4B4A]">{pdfError}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pages.map((src, i) => (
                    <img key={i} src={src} alt={`Page ${i + 1}`} data-testid={`submitted-pdf-page-${i + 1}`}
                      className="w-full rounded-xl border border-[#1A1B1A]/[0.06]" />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-4 sm:p-5 space-y-4" data-testid="submitted-form-answers">
              {fields.length === 0 && (
                <p className="text-sm text-[#757675] italic">This form has no recorded questions.</p>
              )}
              {fields.map((field, i) => {
                const response = submission.responses?.[field.id];
                if (field.field_type === "heading") {
                  return <h3 key={field.id || i} className="text-base text-[#1A1B1A] pt-2 pb-1 border-b border-[#1A1B1A]/[0.06]" style={H}>{field.label}</h3>;
                }
                if (field.field_type === "info_text") {
                  return <p key={field.id || i} className="text-xs text-[#4A4B4A] leading-relaxed bg-[#F3F2EE] rounded-lg px-4 py-3">{field.label}</p>;
                }
                if (field.field_type === "signature") {
                  return (
                    <div key={field.id || i}>
                      <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider block mb-1">{field.label}</label>
                      <Signature
                        imageData={submission.signature_data}
                        typed={submission.typed_signature || submission.signature}
                        testid="submitted-patient-signature"
                      />
                    </div>
                  );
                }
                if (field.field_type === "checkbox") {
                  return (
                    <div key={field.id || i} className="flex items-center gap-2.5">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${response ? "bg-[#205B5A] border-[#205B5A]" : "border-[#1A1B1A]/20"}`}>
                        {response && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm text-[#1A1B1A]">{field.label}</span>
                    </div>
                  );
                }
                return (
                  <Answer key={field.id || i} label={field.label}>
                    {response === true ? "Yes" : response === false ? "No" : (response || <span className="text-[#757675] italic">Not answered</span>)}
                  </Answer>
                );
              })}

              {!fields.some((f) => f.field_type === "signature") &&
                (submission.signature_data || submission.typed_signature || submission.signature) && (
                <div>
                  <label className="text-[10px] font-medium text-[#757675] uppercase tracking-wider block mb-1">Patient signature</label>
                  <Signature
                    imageData={submission.signature_data}
                    typed={submission.typed_signature || submission.signature}
                    testid="submitted-patient-signature"
                  />
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-4 sm:p-5">
            <GuardianDetailsPanel submission={submission} />
            <AuditTrailPanel submission={submission} title="Signature Audit Trail" />
            <div className="mt-4">
              <FormReviewPanel
                submission={submission}
                onReviewed={(updated) => {
                  const merged = { ...submission, ...updated };
                  setSubmission(merged);
                  onReviewed?.(merged);
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-[#1A1B1A]/[0.06]">
            <Shield className="w-3.5 h-3.5 text-[#757675]" />
            <span className="text-[10px] text-[#757675]">
              This is a read-only record of the submitted form. It stays available after review.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
